import React, { useState } from 'react';
import { Modal, Typography, Radio, Select, Checkbox, message, Button } from 'antd';
import { IoFilterOutline } from "react-icons/io5";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Text } = Typography;

const ModalExportarClientes = ({ 
    openExport, 
    setOpenExport, 
    clientesFiltrados = [], 
    clientesSeguros = [], 
    opcionesCreadores = [] // Para filtrar por vendedor
}) => {
    const [isExporting, setIsExporting] = useState(false);
    
    // Estado local de configuración
    const [exportConfig, setExportConfig] = useState({
        formato: 'excel',
        alcance: 'filtrados',
        vendedor_id: '',
        columnas: ['documento', 'nombre', 'telefono', 'email', 'ciudad', 'zona', 'saldo_pendiente', 'estado']
    });

    // Formateador de moneda
    const formatearMoneda = (valor) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(valor || 0);
    };

    const prepararDatos = () => {
        let dataBase = [];
        
        if (exportConfig.alcance === 'filtrados') {
            dataBase = clientesFiltrados;
        } else if (exportConfig.alcance === 'todos') {
            dataBase = clientesSeguros;
        } else if (exportConfig.alcance === 'vendedor') {
            if (!exportConfig.vendedor_id) {
                message.warning("Debes seleccionar un vendedor.");
                return [];
            }
            dataBase = clientesSeguros.filter(c => c.creado_por_usuario_id === exportConfig.vendedor_id);
        } else if (exportConfig.alcance === 'deudores') {
            dataBase = clientesSeguros.filter(c => Number(c.saldo_pendiente) > 0);
        }

        if (dataBase.length === 0) {
            message.warning("No hay clientes que coincidan con la selección para exportar.");
            return [];
        }

        const exportData = [];

        dataBase.forEach(cliente => {
            let row = {};
            if (exportConfig.columnas.includes('documento')) row['NIT / Documento'] = cliente.documento || 'N/A';
            if (exportConfig.columnas.includes('nombre')) row['Nombre / Razón Social'] = cliente.nombre || 'N/A';
            if (exportConfig.columnas.includes('nombre_comercial')) row['Nombre Comercial'] = cliente.nombre_comercial || 'N/A';
            if (exportConfig.columnas.includes('telefono')) row['Teléfono'] = cliente.telefono || 'N/A';
            if (exportConfig.columnas.includes('email')) row['Correo Electrónico'] = cliente.email || 'N/A';
            if (exportConfig.columnas.includes('ciudad')) row['Ciudad'] = cliente.ciudad || 'N/A';
            if (exportConfig.columnas.includes('barrio')) row['Barrio / Sector'] = cliente.barrio || 'N/A';
            if (exportConfig.columnas.includes('direccion')) row['Dirección'] = cliente.direccion || 'N/A';
            if (exportConfig.columnas.includes('zona')) row['Zona / Ruta'] = cliente.zona_nombre || 'N/A';
            if (exportConfig.columnas.includes('categoria')) row['Tipo Cliente'] = cliente.categoria_nombre || 'N/A';
            if (exportConfig.columnas.includes('saldo_pendiente')) row['Deuda (Saldo)'] = formatearMoneda(cliente.saldo_pendiente);
            if (exportConfig.columnas.includes('limite_credito')) row['Cupo Aprobado'] = formatearMoneda(cliente.limite_credito);
            if (exportConfig.columnas.includes('creador')) row['Creado Por'] = cliente.creador_nombre || 'N/A';
            if (exportConfig.columnas.includes('estado')) row['Estado'] = cliente.status_credito === 'activo' ? 'Activo' : 'Suspendido';
            
            exportData.push(row);
        });

        return exportData;
    };

    const exportarExcel = (data) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Clientes");
        XLSX.writeFile(workbook, `Clientes_Yugonub_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const exportarCSV = (data) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob(["\uFEFF" + csvOutput], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Clientes_Yugonub_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const exportarPDF = (data) => {
        const doc = new jsPDF('landscape');
        doc.setFontSize(16);
        doc.text("Reporte de Clientes - Yugonub", 14, 15);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generado el: ${new Date().toLocaleDateString()}`, 14, 22);
        
        const columns = Object.keys(data[0]);
        const rows = data.map(obj => Object.values(obj));

        autoTable(doc, {
            head: [columns],
            body: rows,
            startY: 28,
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [34, 186, 187], textColor: [255, 255, 255], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [248, 250, 252] },
        });

        doc.save(`Clientes_Yugonub_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const handleConfirmarExportacion = () => {
        if (exportConfig.columnas.length === 0) {
            return message.error("Debes seleccionar al menos una columna de datos para exportar.");
        }

        setIsExporting(true);
        const dataToExport = prepararDatos();

        if (dataToExport.length === 0) {
            setIsExporting(false);
            return;
        }

        try {
            if (exportConfig.formato === 'excel') exportarExcel(dataToExport);
            else if (exportConfig.formato === 'csv') exportarCSV(dataToExport);
            else if (exportConfig.formato === 'pdf') exportarPDF(dataToExport);
            
            message.success(`${exportConfig.formato.toUpperCase()} generado correctamente.`);
            setOpenExport(false);
        } catch (error) {
            console.error("Error exportando:", error);
            message.error("Error al generar el archivo. Verifica tu conexión o recarga la página.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal
            title={<span style={{ fontWeight: 800, fontSize: '18px', color: '#0f172a' }}>Exportar Base de Clientes</span>}
            open={openExport}
            onCancel={() => !isExporting && setOpenExport(false)}
            footer={[
                <Button key="back" onClick={() => setOpenExport(false)} disabled={isExporting}>
                    Cancelar
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    loading={isExporting} 
                    onClick={handleConfirmarExportacion}
                    style={{ backgroundColor: '#10b981', borderColor: '#10b981', fontWeight: 600, borderRadius: '8px' }}
                >
                    Generar {exportConfig.formato.toUpperCase()}
                </Button>
            ]}
            centered
            width={600}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '20px' }}>
                
                {/* PASO 1 */}
                <div>
                    <Text style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>1. Formato del archivo</Text>
                    <div style={{ marginTop: '10px' }}>
                        <Radio.Group 
                            value={exportConfig.formato} 
                            onChange={e => setExportConfig({...exportConfig, formato: e.target.value})}
                            buttonStyle="solid"
                        >
                            <Radio.Button value="excel" style={{ borderRadius: '6px 0 0 6px' }}>Excel (.xlsx)</Radio.Button>
                            <Radio.Button value="csv">CSV (.csv)</Radio.Button>
                            <Radio.Button value="pdf" style={{ borderRadius: '0 6px 6px 0' }}>PDF (.pdf)</Radio.Button>
                        </Radio.Group>
                    </div>
                </div>

                {/* PASO 2 */}
                <div>
                    <Text style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>2. ¿A quiénes exportar?</Text>
                    <Select 
                        value={exportConfig.alcance} 
                        onChange={v => setExportConfig({...exportConfig, alcance: v, vendedor_id: ''})}
                        style={{ width: '100%', marginTop: '10px', height: '40px' }}
                    >
                        <Select.Option value="filtrados">Vista actual con los filtros aplicados ({clientesFiltrados.length} clientes)</Select.Option>
                        <Select.Option value="todos">Todos los clientes permitidos ({clientesSeguros.length} clientes)</Select.Option>
                        <Select.Option value="deudores">Solo clientes con cuentas por cobrar (Deudores)</Select.Option>
                        {opcionesCreadores.length > 1 && (
                            <Select.Option value="vendedor">Filtrar por un Vendedor específico</Select.Option>
                        )}
                    </Select>

                    {exportConfig.alcance === 'vendedor' && (
                        <div style={{ marginTop: '10px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                            <Text strong style={{ fontSize: '12px', color: '#475569', display: 'block', marginBottom: '6px' }}>Selecciona el vendedor:</Text>
                            <Select
                                placeholder="Elegir vendedor..."
                                value={exportConfig.vendedor_id || undefined}
                                onChange={val => setExportConfig({...exportConfig, vendedor_id: val})}
                                style={{ width: '100%' }}
                                options={opcionesCreadores.map(u => ({ value: u.id, label: u.nombre }))}
                            />
                        </div>
                    )}
                </div>

                {/* PASO 3 */}
                <div>
                    <Text style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>3. Datos a incluir (Columnas)</Text>
                    <div style={{ marginTop: '10px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', maxHeight: '200px', overflowY: 'auto' }} className="scroll-personalizado">
                        <Checkbox.Group 
                            value={exportConfig.columnas} 
                            onChange={v => setExportConfig({...exportConfig, columnas: v})}
                            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}
                        >
                            <Checkbox value="documento">NIT / Documento</Checkbox>
                            <Checkbox value="nombre">Razón Social / Nombre</Checkbox>
                            <Checkbox value="nombre_comercial">Nombre Comercial</Checkbox>
                            <Checkbox value="telefono">Teléfono</Checkbox>
                            <Checkbox value="email">Correo Electrónico</Checkbox>
                            <Checkbox value="ciudad">Ciudad</Checkbox>
                            <Checkbox value="barrio">Barrio / Sector</Checkbox>
                            <Checkbox value="direccion">Dirección Física</Checkbox>
                            <Checkbox value="zona">Zona de Entrega</Checkbox>
                            <Checkbox value="categoria">Tipo de Cliente</Checkbox>
                            <Checkbox value="saldo_pendiente">Deuda Actual</Checkbox>
                            <Checkbox value="limite_credito">Cupo Aprobado</Checkbox>
                            <Checkbox value="creador">Creado Por (Vendedor)</Checkbox>
                            <Checkbox value="estado">Estado (Activo)</Checkbox>
                        </Checkbox.Group>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                        <IoFilterOutline style={{ verticalAlign: 'middle', marginRight: '4px' }}/>
                        Los valores monetarios se exportarán en formato numérico compatible con Excel.
                    </Text>
                </div>
            </div>
        </Modal>
    );
};

export default ModalExportarClientes;